// Level configurations - 50 levels total
// Levels 1-10: 8x8 grid
// Levels 11-19: 10x10 grid
// Levels 20-24: 10x10 grid with one 2x2 gap
// Levels 25-50: 10x10 grid with two 2x2 gaps

// Gap pattern definitions for harder levels
// Each gap is defined by {row, col, width, height}
export const GAP_PATTERNS = {
  // Single 2x2 gap in center-left area
  singleGap: [
    { row: 4, col: 1, width: 2, height: 2 }
  ],
  // Two 2x2 gaps on opposite sides
  doubleGap: [
    { row: 2, col: 1, width: 2, height: 2 },  // Top-left area
    { row: 6, col: 7, width: 2, height: 2 }   // Bottom-right area
  ],
  // Alternative double gap pattern
  doubleGapAlt: [
    { row: 1, col: 7, width: 2, height: 2 },  // Top-right area
    { row: 7, col: 1, width: 2, height: 2 }   // Bottom-left area
  ],
  // Triple gap pattern for extra hard levels
  tripleGap: [
    { row: 1, col: 4, width: 2, height: 2 },  // Top-center
    { row: 4, col: 1, width: 2, height: 2 },  // Middle-left
    { row: 7, col: 7, width: 2, height: 2 }   // Bottom-right
  ]
};

// Helper function to get gaps for a level
export const getGapsForLevel = (levelId) => {
  if (levelId < 20) return [];
  if (levelId < 25) return GAP_PATTERNS.singleGap;
  if (levelId < 35) return levelId % 2 === 0 ? GAP_PATTERNS.doubleGap : GAP_PATTERNS.doubleGapAlt;
  if (levelId <= 50) return GAP_PATTERNS.tripleGap;
  // Levels 51-100 don't use traditional gaps, they use obstacles
  return [];
};

// Generate obstacle positions for a level
export const getObstaclePositions = (levelConfig, boardSize = 10) => {
  if (!levelConfig?.obstacles) return { 
    ice: [], chain: [], chocolate: [], honey: [], vines: [], tree_sap: [], bird_nest: [], hollow_knot: [],
    // Ocean Depths
    bubbles: [], octopus_ink: [], anchors: [], currents: [],
    // Frozen Peaks
    frozen_fruits: [], snowdrift: [], blizzard: [], avalanche: [],
    // Ancient Ruins
    stone_idols: [], sand_traps: [], curse_tiles: [], lock_key: []
  };
  
  const obstacles = levelConfig.obstacles;
  const positions = { 
    ice: [], chain: [], chocolate: [], honey: [], vines: [], tree_sap: [], bird_nest: [], hollow_knot: [],
    // Ocean Depths
    bubbles: [], octopus_ink: [], anchors: [], currents: [],
    // Frozen Peaks
    frozen_fruits: [], snowdrift: [], blizzard: [], avalanche: [],
    // Ancient Ruins
    stone_idols: [], sand_traps: [], curse_tiles: [], lock_key: []
  };
  const usedPositions = new Set();
  
  // Helper to generate random position
  const getRandomPosition = () => {
    let attempts = 0;
    while (attempts < 100) {
      const row = Math.floor(Math.random() * boardSize);
      const col = Math.floor(Math.random() * boardSize);
      const key = `${row},${col}`;
      if (!usedPositions.has(key)) {
        usedPositions.add(key);
        return { row, col };
      }
      attempts++;
    }
    return null;
  };
  
  // Helper to generate connected vine positions (2-4 cells in a group)
  const generateVineGroup = () => {
    const startPos = getRandomPosition();
    if (!startPos) return [];
    
    const vineGroup = [startPos];
    const groupSize = 2 + Math.floor(Math.random() * 3); // 2-4 cells
    const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];
    
    for (let i = 1; i < groupSize; i++) {
      const lastPos = vineGroup[vineGroup.length - 1];
      const shuffledDirs = directions.sort(() => Math.random() - 0.5);
      
      for (const [dr, dc] of shuffledDirs) {
        const newRow = lastPos.row + dr;
        const newCol = lastPos.col + dc;
        const key = `${newRow},${newCol}`;
        
        if (newRow >= 0 && newRow < boardSize && newCol >= 0 && newCol < boardSize && !usedPositions.has(key)) {
          usedPositions.add(key);
          vineGroup.push({ row: newRow, col: newCol });
          break;
        }
      }
    }
    
    return vineGroup;
  };
  
  // Generate positions based on obstacle type
  if (obstacles.type === 'ice') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.ice.push({ ...pos, layers: obstacles.layers || 1 });
    }
  } else if (obstacles.type === 'chain') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.chain.push(pos);
    }
  } else if (obstacles.type === 'chocolate') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.chocolate.push(pos);
    }
  } else if (obstacles.type === 'honey') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.honey.push(pos);
    }
  } else if (obstacles.type === 'vines') {
    // Generate vine groups
    const vineCount = obstacles.count || 8;
    let vinesPlaced = 0;
    while (vinesPlaced < vineCount) {
      const vineGroup = generateVineGroup();
      const groupId = 'vine-group-' + Math.random().toString(36).substr(2, 6);
      vineGroup.forEach(pos => {
        positions.vines.push({ ...pos, vineGroupId: groupId });
        vinesPlaced++;
      });
    }
  } else if (obstacles.type === 'tree_sap') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.tree_sap.push(pos);
    }
  } else if (obstacles.type === 'bird_nest') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.bird_nest.push(pos);
    }
  // Ocean Depths obstacles
  } else if (obstacles.type === 'bubbles') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.bubbles.push(pos);
    }
  } else if (obstacles.type === 'octopus_ink') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.octopus_ink.push(pos);
    }
  } else if (obstacles.type === 'anchors') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.anchors.push(pos);
    }
  // Frozen Peaks obstacles
  } else if (obstacles.type === 'frozen_fruits') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.frozen_fruits.push(pos);
    }
  } else if (obstacles.type === 'snowdrift') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.snowdrift.push(pos);
    }
  // Ancient Ruins obstacles
  } else if (obstacles.type === 'stone_idols') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.stone_idols.push(pos);
    }
  } else if (obstacles.type === 'curse_tiles') {
    for (let i = 0; i < obstacles.count; i++) {
      const pos = getRandomPosition();
      if (pos) positions.curse_tiles.push(pos);
    }
  } else {
    // Mixed obstacles + fallback for any unrecognized world type
    // (volcanic, moonlight, cosmic, casino, phoenix, etc.)
    // Processes any known obstacle properties present on the obstacles object.
    if (obstacles.ice) {
      for (let i = 0; i < obstacles.ice; i++) {
        const pos = getRandomPosition();
        if (pos) positions.ice.push({ ...pos, layers: 2 });
      }
    }
    if (obstacles.chain) {
      for (let i = 0; i < obstacles.chain; i++) {
        const pos = getRandomPosition();
        if (pos) positions.chain.push(pos);
      }
    }
    if (obstacles.chocolate) {
      for (let i = 0; i < obstacles.chocolate; i++) {
        const pos = getRandomPosition();
        if (pos) positions.chocolate.push(pos);
      }
    }
    if (obstacles.honey) {
      for (let i = 0; i < obstacles.honey; i++) {
        const pos = getRandomPosition();
        if (pos) positions.honey.push(pos);
      }
    }
    // Dark Forest obstacles
    if (obstacles.vines) {
      let vinesPlaced = 0;
      while (vinesPlaced < obstacles.vines) {
        const vineGroup = generateVineGroup();
        const groupId = 'vine-group-' + Math.random().toString(36).substr(2, 6);
        vineGroup.forEach(pos => {
          positions.vines.push({ ...pos, vineGroupId: groupId });
          vinesPlaced++;
        });
      }
    }
    if (obstacles.tree_sap) {
      for (let i = 0; i < obstacles.tree_sap; i++) {
        const pos = getRandomPosition();
        if (pos) positions.tree_sap.push(pos);
      }
    }
    if (obstacles.bird_nest) {
      for (let i = 0; i < obstacles.bird_nest; i++) {
        const pos = getRandomPosition();
        if (pos) positions.bird_nest.push(pos);
      }
    }
    if (obstacles.hollow_knot) {
      // Hollow knots come in pairs (teleport portals)
      const knotCount = obstacles.hollow_knot;
      for (let i = 0; i < knotCount; i += 2) {
        const pos1 = getRandomPosition();
        const pos2 = getRandomPosition();
        if (pos1 && pos2) {
          const linkId = 'knot-link-' + Math.random().toString(36).substr(2, 6);
          positions.hollow_knot.push({ ...pos1, linkedKnotId: linkId, pairIndex: 0 });
          positions.hollow_knot.push({ ...pos2, linkedKnotId: linkId, pairIndex: 1 });
        }
      }
    }
    // === OCEAN DEPTHS OBSTACLES (141-160) ===
    if (obstacles.bubbles) {
      for (let i = 0; i < obstacles.bubbles; i++) {
        const pos = getRandomPosition();
        if (pos) positions.bubbles.push(pos);
      }
    }
    if (obstacles.octopus_ink) {
      for (let i = 0; i < obstacles.octopus_ink; i++) {
        const pos = getRandomPosition();
        if (pos) positions.octopus_ink.push(pos);
      }
    }
    if (obstacles.anchors) {
      for (let i = 0; i < obstacles.anchors; i++) {
        const pos = getRandomPosition();
        if (pos) positions.anchors.push(pos);
      }
    }
    if (obstacles.currents) {
      for (let i = 0; i < obstacles.currents; i++) {
        const pos = getRandomPosition();
        if (pos) positions.currents.push({ ...pos, direction: i % 2 === 0 ? 'right' : 'left' });
      }
    }
    // === FROZEN PEAKS OBSTACLES (161-180) ===
    if (obstacles.frozen_fruits) {
      for (let i = 0; i < obstacles.frozen_fruits; i++) {
        const pos = getRandomPosition();
        if (pos) positions.frozen_fruits.push(pos);
      }
    }
    if (obstacles.snowdrift) {
      for (let i = 0; i < obstacles.snowdrift; i++) {
        const pos = getRandomPosition();
        if (pos) positions.snowdrift.push(pos);
      }
    }
    if (obstacles.blizzard) {
      for (let i = 0; i < obstacles.blizzard; i++) {
        const pos = getRandomPosition();
        if (pos) positions.blizzard.push(pos);
      }
    }
    if (obstacles.avalanche) {
      for (let i = 0; i < obstacles.avalanche; i++) {
        const pos = getRandomPosition();
        if (pos) positions.avalanche.push(pos);
      }
    }
    // === ANCIENT RUINS OBSTACLES (181-200) ===
    if (obstacles.stone_idols) {
      for (let i = 0; i < obstacles.stone_idols; i++) {
        const pos = getRandomPosition();
        if (pos) positions.stone_idols.push(pos);
      }
    }
    if (obstacles.sand_traps) {
      for (let i = 0; i < obstacles.sand_traps; i++) {
        const pos = getRandomPosition();
        if (pos) positions.sand_traps.push(pos);
      }
    }
    if (obstacles.curse_tiles) {
      for (let i = 0; i < obstacles.curse_tiles; i++) {
        const pos = getRandomPosition();
        if (pos) positions.curse_tiles.push(pos);
      }
    }
    if (obstacles.lock_key) {
      // Lock and key pairs
      const keyCount = obstacles.lock_key;
      for (let i = 0; i < keyCount; i += 2) {
        const lockPos = getRandomPosition();
        const keyPos = getRandomPosition();
        if (lockPos && keyPos) {
          const lockId = 'lock-' + Math.random().toString(36).substr(2, 6);
          positions.lock_key.push({ ...lockPos, isKey: false, lockId });
          positions.lock_key.push({ ...keyPos, isKey: true, lockId });
        }
      }
    }
  }
  
  return positions;
};

// Get ingredient start positions (top row)
export const getIngredientPositions = (levelConfig, boardSize = 10) => {
  if (!levelConfig?.ingredients) return [];
  
  const positions = [];
  const count = levelConfig.ingredients.count;
  const usedCols = new Set();
  
  for (let i = 0; i < count; i++) {
    let col;
    let attempts = 0;
    do {
      col = Math.floor(Math.random() * boardSize);
      attempts++;
    } while (usedCols.has(col) && attempts < 20);
    
    usedCols.add(col);
    positions.push({ row: 0, col, emoji: levelConfig.ingredients.emoji });
  }
  
  return positions;
};

// Mission type display helpers
export const getMissionDescription = (levelConfig) => {
  const missionType = levelConfig.missionType || 'collect_fruit';
  const count = levelConfig.targetCount;
  const emoji = levelConfig.targetEmoji || '🎯';
  
  switch (missionType) {
    case 'collect_fruit':
      return `Collect ${count} ${emoji}`;
    case 'create_striped':
      return `Create ${count} Striped Fruits ${emoji}`;
    case 'create_wrapped':
      return `Create ${count} Wrapped Fruits ${emoji}`;
    case 'create_disco':
      return `Create ${count} Disco Melons ${emoji}`;
    case 'break_ice':
      return `Break ${count} Ice Blocks ${emoji}`;
    case 'break_chains':
      return `Break ${count} Chains ${emoji}`;
    case 'clear_chocolate':
      return `Clear ${count} Chocolate ${emoji}`;
    case 'drop_ingredient':
      // Legacy mission type - no longer used in new levels
      return `Drop ${count} ${emoji} to bottom`;
    case 'reach_score':
      const targetScore = levelConfig.targetCount || levelConfig.targetScore || count || 0;
      return `Reach ${targetScore.toLocaleString()} points 🎯`;
    case 'combo_mission':
      return `Use ${count} Special Combos ${emoji}`;
    case 'boss_fight':
      const bossName = levelConfig?.boss?.name || 'the Boss';
      const bossEmoji = levelConfig?.boss?.type === 'owl' ? '🦉' : '🐉';
      return `Defeat ${bossName} ${bossEmoji}`;
    case 'dual_collect':
      if (levelConfig?.dualMission) {
        const obj1 = levelConfig.dualMission.objective1;
        const obj2 = levelConfig.dualMission.objective2;
        return `Collect ${obj1.count} ${obj1.emoji} and ${obj2.count} ${obj2.emoji}`;
      }
      return `Collect multiple fruits`;
    default:
      return `Collect ${count} ${emoji}`;
  }
};

export const LEVELS = [
  // === LEVELS 1-10: 8x8 GRID (EASY) ===
  {
    id: 1,
    name: "Level 1",
    subtitle: "Banana Bonanza",
    difficulty: "Easy",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 8,
    moves: 45,
    boardSize: 8,
    bgColor: "from-yellow-100 to-amber-100",
    borderColor: "border-yellow-400",
  },
  {
    id: 2,
    name: "Level 2",
    subtitle: "Apple Avalanche",
    difficulty: "Easy",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 14,
    moves: 39,
    boardSize: 8,
    bgColor: "from-red-100 to-rose-100",
    borderColor: "border-red-400",
  },
  {
    id: 3,
    name: "Level 3",
    subtitle: "Pineapple Paradise",
    difficulty: "Easy",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 16,
    moves: 36,
    boardSize: 8,
    bgColor: "from-amber-100 to-orange-100",
    borderColor: "border-amber-500",
  },
  {
    id: 4,
    name: "Level 4",
    subtitle: "Pear Pursuit",
    difficulty: "Easy",
    targetFruit: "pear",
    targetEmoji: "🍐",
    targetCount: 17,
    moves: 36,
    boardSize: 8,
    bgColor: "from-green-100 to-lime-100",
    borderColor: "border-green-400",
  },
  {
    id: 5,
    name: "Level 5",
    subtitle: "Orange Overdrive",
    difficulty: "Easy",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 20,
    moves: 36,
    boardSize: 8,
    bgColor: "from-orange-100 to-amber-100",
    borderColor: "border-orange-400",
  },
  {
    id: 6,
    name: "Level 6",
    subtitle: "Peach Party",
    difficulty: "Easy",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 20,
    moves: 39,
    boardSize: 8,
    bgColor: "from-purple-100 to-pink-100",
    borderColor: "border-purple-400",
  },
  {
    id: 7,
    name: "Level 7",
    subtitle: "Banana Blitz",
    difficulty: "Easy",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 24,
    moves: 39,
    boardSize: 8,
    bgColor: "from-yellow-100 to-orange-100",
    borderColor: "border-yellow-500",
  },
  {
    id: 8,
    name: "Level 8",
    subtitle: "Apple Apocalypse",
    difficulty: "Easy",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 25,
    moves: 39,
    boardSize: 8,
    bgColor: "from-red-100 to-pink-100",
    borderColor: "border-red-500",
  },
  {
    id: 9,
    name: "Level 9",
    subtitle: "Pineapple Panic",
    difficulty: "Easy",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 28,
    moves: 41,
    boardSize: 8,
    bgColor: "from-amber-100 to-yellow-100",
    borderColor: "border-amber-600",
  },
  {
    id: 10,
    name: "Level 10",
    subtitle: "Fruit Frenzy",
    difficulty: "Easy",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 38,
    moves: 32,
    boardSize: 8,
    bgColor: "from-orange-100 to-red-100",
    borderColor: "border-orange-600",
  },

  // === LEVELS 11-20: 10x10 GRID - NEW FRUITS ===
  {
    id: 11,
    name: "Level 11",
    subtitle: "Strawberry Splash",
    difficulty: "Easy",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 40,
    moves: 45,
    boardSize: 10,
    bgColor: "from-red-100 to-pink-100",
    borderColor: "border-red-400",
  },
  {
    id: 12,
    name: "Level 12",
    subtitle: "Lemon Lightning",
    difficulty: "Easy",
    targetFruit: "lemon",
    targetEmoji: "🍋",
    targetCount: 44,
    moves: 45,
    boardSize: 10,
    bgColor: "from-yellow-100 to-lime-100",
    borderColor: "border-yellow-400",
  },
  {
    id: 13,
    name: "Level 13",
    subtitle: "Cherry Chaos",
    difficulty: "Easy",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 40,
    moves: 41,
    boardSize: 10,
    bgColor: "from-rose-100 to-red-100",
    borderColor: "border-rose-500",
  },
  {
    id: 14,
    name: "Level 14",
    subtitle: "Watermelon Wave",
    difficulty: "Easy",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 48,
    moves: 49,
    boardSize: 10,
    bgColor: "from-green-100 to-red-100",
    borderColor: "border-green-500",
  },
  {
    id: 15,
    name: "Level 15",
    subtitle: "Mango Madness",
    difficulty: "Easy",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 52,
    moves: 52,
    boardSize: 10,
    bgColor: "from-orange-100 to-yellow-100",
    borderColor: "border-orange-500",
  },
  {
    id: 16,
    name: "Level 16",
    subtitle: "Kiwi Krush",
    difficulty: "Easy",
    targetFruit: "kiwi",
    targetEmoji: "🥝",
    targetCount: 48,
    moves: 49,
    boardSize: 10,
    bgColor: "from-green-100 to-emerald-100",
    borderColor: "border-green-600",
  },
  {
    id: 17,
    name: "Level 17",
    subtitle: "Blueberry Blast",
    difficulty: "Easy",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 44,
    moves: 45,
    boardSize: 10,
    bgColor: "from-blue-100 to-indigo-100",
    borderColor: "border-blue-500",
  },
  {
    id: 18,
    name: "Level 18",
    subtitle: "Strawberry Storm",
    difficulty: "Easy",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 60,
    moves: 52,
    boardSize: 10,
    bgColor: "from-pink-100 to-rose-100",
    borderColor: "border-pink-500",
  },
  {
    id: 19,
    name: "Level 19",
    subtitle: "Lemon Legend",
    difficulty: "Easy",
    targetFruit: "lemon",
    targetEmoji: "🍋",
    targetCount: 64,
    moves: 54,
    boardSize: 10,
    bgColor: "from-lime-100 to-yellow-100",
    borderColor: "border-lime-500",
  },
  {
    id: 20,
    name: "Level 20",
    subtitle: "Tropical Thunder",
    difficulty: "Easy",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 85,
    moves: 45,
    boardSize: 10,
    bgColor: "from-amber-100 to-orange-100",
    borderColor: "border-amber-500",
  },

  // === LEVELS 21-30: 10x10 GRID - MIXED CHALLENGES ===
  {
    id: 21,
    name: "Level 21",
    subtitle: "Cherry Challenge",
    difficulty: "Easy",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 56,
    moves: 52,
    boardSize: 10,
    bgColor: "from-red-100 to-rose-100",
    borderColor: "border-red-600",
  },
  {
    id: 22,
    name: "Level 22",
    subtitle: "Watermelon Warfare",
    difficulty: "Easy",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 64,
    moves: 54,
    boardSize: 10,
    bgColor: "from-emerald-100 to-green-100",
    borderColor: "border-emerald-500",
  },
  {
    id: 23,
    name: "Level 23",
    subtitle: "Kiwi Conquest",
    difficulty: "Easy",
    targetFruit: "kiwi",
    targetEmoji: "🥝",
    targetCount: 60,
    moves: 52,
    boardSize: 10,
    bgColor: "from-lime-100 to-green-100",
    borderColor: "border-lime-600",
  },
  {
    id: 24,
    name: "Level 24",
    subtitle: "Blueberry Bonanza",
    difficulty: "Easy",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 56,
    moves: 49,
    boardSize: 10,
    bgColor: "from-indigo-100 to-blue-100",
    borderColor: "border-indigo-500",
  },
  {
    id: 25,
    name: "Level 25",
    subtitle: "Peach Perfection",
    difficulty: "Easy",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 60,
    moves: 52,
    boardSize: 10,
    bgColor: "from-purple-100 to-violet-100",
    borderColor: "border-purple-500",
  },
  {
    id: 26,
    name: "Level 26",
    subtitle: "Orange Obsession",
    difficulty: "Easy",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 64,
    moves: 54,
    boardSize: 10,
    bgColor: "from-orange-100 to-amber-100",
    borderColor: "border-orange-600",
  },
  {
    id: 27,
    name: "Level 27",
    subtitle: "Pear Pandemonium",
    difficulty: "Easy",
    targetFruit: "pear",
    targetEmoji: "🍐",
    targetCount: 68,
    moves: 57,
    boardSize: 10,
    bgColor: "from-green-100 to-lime-100",
    borderColor: "border-green-500",
  },
  {
    id: 28,
    name: "Level 28",
    subtitle: "Apple Armageddon",
    difficulty: "Easy",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 72,
    moves: 58,
    boardSize: 10,
    bgColor: "from-red-100 to-pink-100",
    borderColor: "border-red-600",
  },
  {
    id: 29,
    name: "Level 29",
    subtitle: "Banana Breakdown",
    difficulty: "Easy",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 72,
    moves: 58,
    boardSize: 10,
    bgColor: "from-yellow-100 to-amber-100",
    borderColor: "border-yellow-600",
  },
  {
    id: 30,
    name: "Level 30",
    subtitle: "Fruit Fiesta",
    difficulty: "Easy",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 76,
    moves: 62,
    boardSize: 10,
    bgColor: "from-amber-100 to-yellow-100",
    borderColor: "border-amber-600",
  },

  // === LEVELS 31-40: 10x10 GRID - MASTER LEVELS ===
  {
    id: 31,
    name: "Level 31",
    subtitle: "Strawberry Saga",
    difficulty: "Easy",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 68,
    moves: 54,
    boardSize: 10,
    bgColor: "from-rose-100 to-red-100",
    borderColor: "border-rose-600",
  },
  {
    id: 32,
    name: "Level 32",
    subtitle: "Lemon Legacy",
    difficulty: "Easy",
    targetFruit: "lemon",
    targetEmoji: "🍋",
    targetCount: 72,
    moves: 57,
    boardSize: 10,
    bgColor: "from-yellow-100 to-lime-100",
    borderColor: "border-yellow-500",
  },
  {
    id: 33,
    name: "Level 33",
    subtitle: "Cherry Champion",
    difficulty: "Easy",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 64,
    moves: 52,
    boardSize: 10,
    bgColor: "from-red-100 to-rose-100",
    borderColor: "border-rose-700",
  },
  {
    id: 34,
    name: "Level 34",
    subtitle: "Watermelon Wonder",
    difficulty: "Easy",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 76,
    moves: 59,
    boardSize: 10,
    bgColor: "from-green-100 to-emerald-100",
    borderColor: "border-green-600",
  },
  {
    id: 35,
    name: "Level 35",
    subtitle: "Mango Mayhem",
    difficulty: "Easy",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 80,
    moves: 62,
    boardSize: 10,
    bgColor: "from-orange-100 to-amber-100",
    borderColor: "border-orange-600",
  },
  {
    id: 36,
    name: "Level 36",
    subtitle: "Kiwi Kingdom",
    difficulty: "Easy",
    targetFruit: "kiwi",
    targetEmoji: "🥝",
    targetCount: 72,
    moves: 57,
    boardSize: 10,
    bgColor: "from-emerald-100 to-green-100",
    borderColor: "border-emerald-600",
  },
  {
    id: 37,
    name: "Level 37",
    subtitle: "Blueberry Blitz",
    difficulty: "Easy",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 68,
    moves: 54,
    boardSize: 10,
    bgColor: "from-blue-100 to-indigo-100",
    borderColor: "border-blue-600",
  },
  {
    id: 38,
    name: "Level 38",
    subtitle: "Peach Pinnacle",
    difficulty: "Easy",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 76,
    moves: 58,
    boardSize: 10,
    bgColor: "from-violet-100 to-purple-100",
    borderColor: "border-violet-500",
  },
  {
    id: 39,
    name: "Level 39",
    subtitle: "Orange Odyssey",
    difficulty: "Easy",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 80,
    moves: 62,
    boardSize: 10,
    bgColor: "from-amber-100 to-orange-100",
    borderColor: "border-amber-700",
  },
  {
    id: 40,
    name: "Level 40",
    subtitle: "Ultimate Fruit Mix",
    difficulty: "Easy",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 84,
    moves: 65,
    boardSize: 10,
    bgColor: "from-yellow-100 to-amber-100",
    borderColor: "border-yellow-700",
  },

  // === LEVELS 41-50: 10x10 GRID - LEGENDARY LEVELS ===
  {
    id: 41,
    name: "Level 41",
    subtitle: "Strawberry Supreme",
    difficulty: "Medium",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 90,
    moves: 52,
    boardSize: 10,
    bgColor: "from-pink-100 to-rose-100",
    borderColor: "border-pink-600",
  },
  {
    id: 42,
    name: "Level 42",
    subtitle: "Lemon Lord",
    difficulty: "Medium",
    targetFruit: "lemon",
    targetEmoji: "🍋",
    targetCount: 94,
    moves: 55,
    boardSize: 10,
    bgColor: "from-lime-100 to-yellow-100",
    borderColor: "border-lime-600",
  },
  {
    id: 43,
    name: "Level 43",
    subtitle: "Cherry Crusher",
    difficulty: "Medium",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 85,
    moves: 49,
    boardSize: 10,
    bgColor: "from-red-100 to-rose-100",
    borderColor: "border-rose-800",
  },
  {
    id: 44,
    name: "Level 44",
    subtitle: "Watermelon Wipeout",
    difficulty: "Medium",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 99,
    moves: 57,
    boardSize: 10,
    bgColor: "from-emerald-100 to-green-100",
    borderColor: "border-emerald-700",
  },
  {
    id: 45,
    name: "Level 45",
    subtitle: "Mango Master",
    difficulty: "Medium",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 103,
    moves: 60,
    boardSize: 10,
    bgColor: "from-orange-100 to-yellow-100",
    borderColor: "border-orange-700",
  },
  {
    id: 46,
    name: "Level 46",
    subtitle: "Kiwi King",
    difficulty: "Medium",
    targetFruit: "kiwi",
    targetEmoji: "🥝",
    targetCount: 94,
    moves: 55,
    boardSize: 10,
    bgColor: "from-green-100 to-lime-100",
    borderColor: "border-green-700",
  },
  {
    id: 47,
    name: "Level 47",
    subtitle: "Blueberry Boss",
    difficulty: "Medium",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 90,
    moves: 52,
    boardSize: 10,
    bgColor: "from-indigo-100 to-blue-100",
    borderColor: "border-indigo-600",
  },
  {
    id: 48,
    name: "Level 48",
    subtitle: "Peach Prodigy",
    difficulty: "Medium",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 99,
    moves: 57,
    boardSize: 10,
    bgColor: "from-purple-100 to-violet-100",
    borderColor: "border-purple-600",
  },
  {
    id: 49,
    name: "Level 49",
    subtitle: "Tropical Titan",
    difficulty: "Medium",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 108,
    moves: 60,
    boardSize: 10,
    bgColor: "from-yellow-100 to-orange-100",
    borderColor: "border-yellow-700",
  },
  {
    id: 50,
    name: "Level 50",
    subtitle: "Prestige Portal",
    difficulty: "Medium",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 130,
    moves: 60,
    boardSize: 10,
    bgColor: "from-amber-100 via-yellow-100 to-orange-100",
    borderColor: "border-amber-700",
  },
  
  // === LEVELS 51-60: ICE WORLD (10x10 with Ice Obstacles) ===
  {
    id: 51,
    name: "Level 51",
    subtitle: "Frozen Frontier",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "blueberry",
    targetEmoji: "🧊",
    targetCount: 13, // Ice blocks to break
    moves: 38,
    boardSize: 10,
    obstacles: { type: "ice", count: 15, layers: 1 },
    bgColor: "from-cyan-100 to-blue-100",
    borderColor: "border-cyan-500",
  },
  {
    id: 52,
    name: "Level 52",
    subtitle: "Crystal Cave",
    difficulty: "Medium",
    missionType: "create_striped",
    targetFruit: "cherry",
    targetEmoji: "⚡",
    targetCount: 5, // Striped fruits to create
    moves: 33,
    boardSize: 10,
    obstacles: { type: "ice", count: 10, layers: 1 },
    bgColor: "from-blue-100 to-indigo-100",
    borderColor: "border-blue-500",
  },
  {
    id: 53,
    name: "Level 53",
    subtitle: "Glacier Garden",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "apple",
    targetEmoji: "🧊",
    targetCount: 18,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "ice", count: 20, layers: 2 },
    bgColor: "from-sky-100 to-cyan-100",
    borderColor: "border-sky-500",
  },
  {
    id: 54,
    name: "Level 54",
    subtitle: "Snowflake Sprint",
    difficulty: "Medium",
    missionType: "create_wrapped",
    targetFruit: "strawberry",
    targetEmoji: "🎁",
    targetCount: 5,
    moves: 35,
    boardSize: 10,
    obstacles: { type: "ice", count: 12, layers: 1 },
    bgColor: "from-slate-100 to-blue-100",
    borderColor: "border-slate-400",
  },
  {
    id: 55,
    name: "Level 55",
    subtitle: "Ice Breaker",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "watermelon",
    targetEmoji: "🧊",
    targetCount: 22,
    moves: 49,
    boardSize: 10,
    obstacles: { type: "ice", count: 25, layers: 2 },
    bgColor: "from-cyan-100 to-teal-100",
    borderColor: "border-cyan-600",
  },
  {
    id: 56,
    name: "Level 56",
    subtitle: "Frosty Falls",
    difficulty: "Medium",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 45,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "ice", count: 8, layers: 1 },
    bgColor: "from-blue-100 to-purple-100",
    borderColor: "border-blue-600",
  },
  {
    id: 57,
    name: "Level 57",
    subtitle: "Arctic Adventure",
    difficulty: "Medium",
    missionType: "create_striped",
    targetFruit: "lemon",
    targetEmoji: "⚡",
    targetCount: 7,
    moves: 41,
    boardSize: 10,
    obstacles: { type: "ice", count: 18, layers: 2 },
    bgColor: "from-indigo-100 to-blue-100",
    borderColor: "border-indigo-500",
  },
  {
    id: 58,
    name: "Level 58",
    subtitle: "Permafrost Palace",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "pear",
    targetEmoji: "🧊",
    targetCount: 27,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "ice", count: 30, layers: 3 },
    bgColor: "from-violet-100 to-blue-100",
    borderColor: "border-violet-500",
  },
  {
    id: 59,
    name: "Level 59",
    subtitle: "Blizzard Bash",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "ice",
    targetEmoji: "🧊",
    targetCount: 30,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "ice", count: 15, layers: 2 },
    bgColor: "from-blue-100 to-cyan-100",
    borderColor: "border-blue-700",
  },
  {
    id: 60,
    name: "Level 60",
    subtitle: "Ice Age Boss",
    difficulty: "Medium",
    missionType: "break_ice",
    targetFruit: "mango",
    targetEmoji: "🧊",
    targetCount: 36,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "ice", count: 40, layers: 3 },
    bgColor: "from-cyan-200 to-blue-200",
    borderColor: "border-cyan-700",
  },

  // === LEVELS 61-70: CHAIN REALM (10x10 with Chains) ===
  {
    id: 61,
    name: "Level 61",
    subtitle: "Chain Reaction",
    difficulty: "Medium",
    missionType: "break_chains",
    targetFruit: "banana",
    targetEmoji: "⛓️",
    targetCount: 10,
    moves: 33,
    boardSize: 10,
    obstacles: { type: "chain", count: 12 },
    bgColor: "from-gray-100 to-slate-200",
    borderColor: "border-gray-500",
  },
  {
    id: 62,
    name: "Level 62",
    subtitle: "Lock & Key",
    difficulty: "Medium",
    missionType: "create_wrapped",
    targetFruit: "apple",
    targetEmoji: "🎁",
    targetCount: 5,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "chain", count: 15 },
    bgColor: "from-amber-100 to-yellow-100",
    borderColor: "border-amber-500",
  },
  {
    id: 63,
    name: "Level 63",
    subtitle: "Shackle Shuffle",
    difficulty: "Medium",
    missionType: "break_chains",
    targetFruit: "chain",
    targetEmoji: "⛓️",
    targetCount: 20,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "chain", count: 20 },
    bgColor: "from-stone-100 to-gray-200",
    borderColor: "border-stone-500",
  },
  {
    id: 64,
    name: "Level 64",
    subtitle: "Freedom Fighter",
    difficulty: "Medium",
    missionType: "break_chains",
    targetFruit: "strawberry",
    targetEmoji: "⛓️",
    targetCount: 16,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "chain", count: 18 },
    bgColor: "from-zinc-100 to-slate-200",
    borderColor: "border-zinc-500",
  },
  {
    id: 65,
    name: "Level 65",
    subtitle: "Bound & Determined",
    difficulty: "Medium",
    missionType: "create_striped",
    targetFruit: "cherry",
    targetEmoji: "⚡",
    targetCount: 5,
    moves: 49,
    boardSize: 10,
    obstacles: { type: "chain", count: 12 },
    bgColor: "from-gray-200 to-slate-300",
    borderColor: "border-gray-600",
  },
  {
    id: 66,
    name: "Level 66",
    subtitle: "Unbreakable",
    difficulty: "Medium",
    missionType: "create_wrapped",
    targetFruit: "watermelon",
    targetEmoji: "🎁",
    targetCount: 5,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "chain", count: 10 },
    bgColor: "from-neutral-100 to-gray-200",
    borderColor: "border-neutral-500",
  },
  {
    id: 67,
    name: "Level 67",
    subtitle: "Chain Gang",
    difficulty: "Medium",
    missionType: "break_chains",
    targetFruit: "lemon",
    targetEmoji: "⛓️",
    targetCount: 16,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "chain", count: 18 },
    bgColor: "from-slate-200 to-gray-300",
    borderColor: "border-slate-600",
  },
  {
    id: 68,
    name: "Level 68",
    subtitle: "Escape Artist",
    difficulty: "Medium",
    missionType: "collect_fruit",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 18,
    moves: 49,
    boardSize: 10,
    obstacles: { type: "chain", count: 14 },
    bgColor: "from-gray-100 to-zinc-200",
    borderColor: "border-gray-700",
  },
  {
    id: 69,
    name: "Level 69",
    subtitle: "Breaking Free",
    difficulty: "Medium",
    missionType: "collect_fruit",
    targetFruit: "kiwi",
    targetEmoji: "🥝",
    targetCount: 50,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "chain", count: 16 },
    bgColor: "from-stone-200 to-slate-300",
    borderColor: "border-stone-600",
  },
  {
    id: 70,
    name: "Level 70",
    subtitle: "Chain Master",
    difficulty: "Medium",
    missionType: "break_chains",
    targetFruit: "blueberry",
    targetEmoji: "⛓️",
    targetCount: 18,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "chain", count: 20 },
    bgColor: "from-gray-300 to-slate-400",
    borderColor: "border-gray-800",
  },

  // === LEVELS 71-80: CHOCOLATE FACTORY (10x10 with Chocolate) ===
  {
    id: 71,
    name: "Level 71",
    subtitle: "Chocolate Chaos",
    difficulty: "Medium",
    missionType: "clear_chocolate",
    targetFruit: "banana",
    targetEmoji: "🍫",
    targetCount: 9,
    moves: 33,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 10, spreads: true },
    bgColor: "from-amber-200 to-orange-200",
    borderColor: "border-amber-600",
  },
  {
    id: 72,
    name: "Level 72",
    subtitle: "Sweet Surrender",
    difficulty: "Medium",
    missionType: "create_striped",
    targetFruit: "apple",
    targetEmoji: "⚡",
    targetCount: 7,
    moves: 35,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 8, spreads: true },
    bgColor: "from-yellow-200 to-amber-200",
    borderColor: "border-yellow-600",
  },
  {
    id: 73,
    name: "Level 73",
    subtitle: "Cocoa Craze",
    difficulty: "Medium",
    missionType: "clear_chocolate",
    targetFruit: "strawberry",
    targetEmoji: "🍫",
    targetCount: 13,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 15, spreads: true },
    bgColor: "from-orange-200 to-red-200",
    borderColor: "border-orange-600",
  },
  {
    id: 74,
    name: "Level 74",
    subtitle: "Truffle Trouble",
    difficulty: "Medium",
    missionType: "create_wrapped",
    targetFruit: "pear",
    targetEmoji: "🎁",
    targetCount: 6,
    moves: 41,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 12, spreads: true },
    bgColor: "from-amber-300 to-orange-300",
    borderColor: "border-amber-700",
  },
  {
    id: 75,
    name: "Level 75",
    subtitle: "Fudge Fury",
    difficulty: "Medium",
    missionType: "clear_chocolate",
    targetFruit: "chocolate",
    targetEmoji: "🍫",
    targetCount: 10,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 10, spreads: true },
    bgColor: "from-rose-200 to-pink-200",
    borderColor: "border-rose-600",
  },
  {
    id: 76,
    name: "Level 76",
    subtitle: "Brownie Blitz",
    difficulty: "Medium",
    missionType: "clear_chocolate",
    targetFruit: "mango",
    targetEmoji: "🍫",
    targetCount: 18,
    moves: 46,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 20, spreads: true },
    bgColor: "from-orange-300 to-amber-300",
    borderColor: "border-orange-700",
  },
  {
    id: 77,
    name: "Level 77",
    subtitle: "Mocha Madness",
    difficulty: "Medium",
    missionType: "reach_score",
    targetFruit: "lemon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 14, spreads: true },
    bgColor: "from-yellow-300 to-orange-300",
    borderColor: "border-yellow-700",
  },
  {
    id: 78,
    name: "Level 78",
    subtitle: "Ganache Gauntlet",
    difficulty: "Medium",
    missionType: "reach_score",
    targetFruit: "watermelon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 49,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 16, spreads: true },
    bgColor: "from-red-200 to-rose-300",
    borderColor: "border-red-700",
  },
  {
    id: 79,
    name: "Level 79",
    subtitle: "Praline Panic",
    difficulty: "Medium",
    missionType: "reach_score",
    targetFruit: "orange",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 52,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 25, spreads: true },
    bgColor: "from-amber-400 to-orange-400",
    borderColor: "border-amber-800",
  },
  {
    id: 80,
    name: "Level 80",
    subtitle: "Chocolate Boss",
    difficulty: "Medium",
    missionType: "reach_score",
    targetFruit: "pineapple",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 30, spreads: true },
    bgColor: "from-orange-400 to-red-400",
    borderColor: "border-orange-800",
  },

  // === LEVELS 81-90: HONEY HIVE (10x10 with Honey) ===
  {
    id: 81,
    name: "Level 81",
    subtitle: "Sticky Situation",
    difficulty: "Hard",
    missionType: "collect_fruit",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 50,
    moves: 35,
    boardSize: 10,
    obstacles: { type: "honey", count: 12 },
    bgColor: "from-yellow-200 to-amber-200",
    borderColor: "border-yellow-500",
  },
  {
    id: 82,
    name: "Level 82",
    subtitle: "Bee Careful",
    difficulty: "Hard",
    missionType: "create_striped",
    targetFruit: "apple",
    targetEmoji: "⚡",
    targetCount: 12,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "honey", count: 16 },
    bgColor: "from-amber-200 to-yellow-300",
    borderColor: "border-amber-500",
  },
  {
    id: 83,
    name: "Level 83",
    subtitle: "Nectar Quest",
    difficulty: "Hard",
    missionType: "collect_fruit",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 55,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "honey", count: 10 },
    startingSpecials: { striped: 3 },
    bgColor: "from-yellow-300 to-orange-200",
    borderColor: "border-yellow-600",
  },
  {
    id: 84,
    name: "Level 84",
    subtitle: "Honeycomb Havoc",
    difficulty: "Hard",
    missionType: "create_wrapped",
    targetFruit: "strawberry",
    targetEmoji: "🎁",
    targetCount: 5,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "honey", count: 12 },
    startingSpecials: { striped: 3 },
    bgColor: "from-orange-200 to-amber-300",
    borderColor: "border-orange-500",
  },
  {
    id: 85,
    name: "Level 85",
    subtitle: "Buzz Blast",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "cherry",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "honey", count: 12 },
    startingSpecials: { striped: 4 },
    bgColor: "from-yellow-400 to-amber-300",
    borderColor: "border-yellow-700",
  },
  {
    id: 86,
    name: "Level 86",
    subtitle: "Royal Jelly",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "lemon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 100,
    boardSize: 10,
    obstacles: { type: "honey", count: 14 },
    startingSpecials: { striped: 4 },
    bgColor: "from-amber-300 to-yellow-400",
    borderColor: "border-amber-600",
  },
  {
    id: 87,
    name: "Level 87",
    subtitle: "Swarm Storm",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "honey", count: 14 },
    startingSpecials: { striped: 4 },
    bgColor: "from-yellow-400 to-orange-300",
    borderColor: "border-yellow-800",
  },
  {
    id: 88,
    name: "Level 88",
    subtitle: "Amber Alert",
    difficulty: "Hard",
    missionType: "create_wrapped",
    targetFruit: "wrapped",
    targetEmoji: "🎁",
    targetCount: 6,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "honey", count: 16 },
    startingSpecials: { striped: 5 },
    bgColor: "from-orange-300 to-yellow-400",
    borderColor: "border-orange-600",
  },
  {
    id: 89,
    name: "Level 89",
    subtitle: "Hive Mind",
    difficulty: "Hard",
    missionType: "create_striped",
    targetFruit: "blueberry",
    targetEmoji: "⚡",
    targetCount: 8,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "honey", count: 16 },
    startingSpecials: { striped: 5 },
    bgColor: "from-amber-400 to-orange-400",
    borderColor: "border-amber-700",
  },
  {
    id: 90,
    name: "Level 90",
    subtitle: "Queen Bee Boss",
    difficulty: "Hard",
    missionType: "collect_fruit",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 50,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "honey", count: 18 },
    startingSpecials: { striped: 6 },
    bgColor: "from-yellow-500 to-amber-500",
    borderColor: "border-yellow-900",
  },

  // === LEVELS 91-100: ULTIMATE CHALLENGE (Mixed Obstacles) ===
  {
    id: 91,
    name: "Level 91",
    subtitle: "Mixed Mayhem",
    difficulty: "Hard",
    missionType: "break_ice",
    targetFruit: "banana",
    targetEmoji: "🧊",
    targetCount: 20,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 20, chain: 10 },
    bgColor: "from-purple-200 to-pink-200",
    borderColor: "border-purple-500",
  },
  {
    id: 92,
    name: "Level 92",
    subtitle: "Chaos Cascade",
    difficulty: "Hard",
    missionType: "create_striped",
    targetFruit: "apple",
    targetEmoji: "⚡",
    targetCount: 15,
    moves: 70,
    boardSize: 10,
    obstacles: { type: "mixed", chocolate: 12, honey: 8 },
    bgColor: "from-pink-200 to-rose-200",
    borderColor: "border-pink-500",
  },
  {
    id: 93,
    name: "Level 93",
    subtitle: "Storm Surge",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 15000,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 15, chocolate: 10 },
    bgColor: "from-violet-200 to-purple-200",
    borderColor: "border-violet-500",
  },
  {
    id: 94,
    name: "Level 94",
    subtitle: "Triple Threat",
    difficulty: "Hard",
    missionType: "combo_mission",
    targetFruit: "cherry",
    targetEmoji: "💥",
    targetCount: 12,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 10, chain: 10, honey: 10 },
    bgColor: "from-fuchsia-200 to-pink-200",
    borderColor: "border-fuchsia-500",
  },
  {
    id: 95,
    name: "Level 95",
    subtitle: "Inferno Ice",
    difficulty: "Hard",
    missionType: "break_ice",
    targetFruit: "watermelon",
    targetEmoji: "🧊",
    targetCount: 35,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 35, chocolate: 15 },
    bgColor: "from-rose-200 to-red-200",
    borderColor: "border-rose-600",
  },
  {
    id: 96,
    name: "Level 96",
    subtitle: "Nightmare Mode",
    difficulty: "Hard",
    missionType: "create_wrapped",
    targetFruit: "mango",
    targetEmoji: "🎁",
    targetCount: 8,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "mixed", chain: 20, honey: 15 },
    bgColor: "from-purple-300 to-violet-300",
    borderColor: "border-purple-600",
  },
  {
    id: 97,
    name: "Level 97",
    subtitle: "Hell's Kitchen",
    difficulty: "Hard",
    missionType: "clear_chocolate",
    targetFruit: "lemon",
    targetEmoji: "🍫",
    targetCount: 40,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "mixed", chocolate: 40, ice: 10 },
    bgColor: "from-red-300 to-orange-300",
    borderColor: "border-red-600",
  },
  {
    id: 98,
    name: "Level 98",
    subtitle: "Apocalypse",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "pineapple",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 20, chain: 15, chocolate: 10, honey: 10 },
    bgColor: "from-orange-300 to-red-400",
    borderColor: "border-orange-700",
  },
  {
    id: 99,
    name: "Level 99",
    subtitle: "Final Countdown",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 30000,
    moves: 65,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 25, chain: 20, honey: 15 },
    bgColor: "from-indigo-300 to-purple-400",
    borderColor: "border-indigo-700",
  },
  {
    id: 100,
    name: "The Fruit Dragon",
    subtitle: "BOSS BATTLE",
    difficulty: "Boss",
    missionType: "boss_fight",
    targetFruit: "dragon",
    targetEmoji: "🐉",
    targetCount: 1, // Defeat the dragon
    moves: 999, // Unlimited moves (no limit)
    boardSize: 8, // 8x8 for boss fight
    freeRetry: true, // No HP cost for retrying
    boss: {
      name: "Fruit Dragon",
      image: "https://static.prod-images.emergentagent.com/jobs/3abb1bb8-5892-4ab0-b039-101ce0760441/images/9c5d91271ba6dcbd904feacfaf3934f170458242976b3f318df2ff075191e97c.png",
      phases: [
        { hp: 5000, weakSpot: "banana", weakEmoji: "🍌", attack: "inferno", attackName: "Inferno" }
      ],
      attackInterval: 5, // Attack every 5 moves
      baseDamage: 8, // Damage per fruit matched
      weakSpotMultiplier: 2.0, // Weak spot does 2x damage
      comboMultiplier: 1.5, // Chain reactions multiply damage
    },
    rewards: {
      stars: 100,
      xp: 5000,
      frame: "dragon_slayer",
      frameName: "Dragon Slayer"
    },
    bgColor: "from-red-600 via-orange-500 to-yellow-500",
    borderColor: "border-red-700",
  },

  // === LEVELS 101-120: POINT WORLD (Score-based Challenges) ===
  // All levels in this world are score-based missions
  {
    id: 101,
    name: "Level 101",
    subtitle: "Point Starter",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "banana",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 30,
    boardSize: 10,
    bgColor: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-600",
  },
  {
    id: 102,
    name: "Level 102",
    subtitle: "Score Seeker",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "apple",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 32,
    boardSize: 10,
    bgColor: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-600",
  },
  {
    id: 103,
    name: "Level 103",
    subtitle: "Point Hunter",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "pear",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 34,
    boardSize: 10,
    obstacles: { type: "ice", count: 5, layers: 1 },
    bgColor: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-600",
  },
  {
    id: 104,
    name: "Level 104",
    subtitle: "Score Crusher",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "orange",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 35,
    boardSize: 10,
    obstacles: { type: "ice", count: 8, layers: 1 },
    bgColor: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-600",
  },
  {
    id: 105,
    name: "Level 105",
    subtitle: "Point Master",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "peach",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 36,
    boardSize: 10,
    obstacles: { type: "chain", count: 10 },
    bgColor: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-600",
  },
  {
    id: 106,
    name: "Level 106",
    subtitle: "Score Champion",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "strawberry",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "chain", count: 12 },
    bgColor: "from-purple-500 to-pink-500",
    borderColor: "border-purple-600",
  },
  {
    id: 107,
    name: "Level 107",
    subtitle: "Point Legend",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "lemon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 8 },
    bgColor: "from-purple-500 to-pink-500",
    borderColor: "border-purple-600",
  },
  {
    id: 108,
    name: "Level 108",
    subtitle: "Score Warrior",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "cherry",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 42,
    boardSize: 10,
    obstacles: { type: "chocolate", count: 10 },
    bgColor: "from-purple-500 to-pink-500",
    borderColor: "border-purple-600",
  },
  {
    id: 109,
    name: "Level 109",
    subtitle: "Point Prodigy",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "watermelon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 44,
    boardSize: 10,
    obstacles: { type: "honey", count: 8 },
    bgColor: "from-purple-500 to-pink-500",
    borderColor: "border-purple-600",
  },
  {
    id: 110,
    name: "Level 110",
    subtitle: "Score Elite",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "honey", count: 12 },
    bgColor: "from-purple-500 to-pink-500",
    borderColor: "border-purple-600",
  },
  {
    id: 111,
    name: "Level 111",
    subtitle: "Point Titan",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "kiwi",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 48,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 5, chain: 5 },
    bgColor: "from-pink-500 to-rose-500",
    borderColor: "border-pink-600",
  },
  {
    id: 112,
    name: "Level 112",
    subtitle: "Score Conqueror",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "blueberry",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 8, chain: 6 },
    bgColor: "from-pink-500 to-rose-500",
    borderColor: "border-pink-600",
  },
  {
    id: 113,
    name: "Level 113",
    subtitle: "Point Emperor",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "banana",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 52,
    boardSize: 10,
    obstacles: { type: "mixed", chocolate: 6, honey: 6 },
    bgColor: "from-pink-500 to-rose-500",
    borderColor: "border-pink-600",
  },
  {
    id: 114,
    name: "Level 114",
    subtitle: "Score Dominator",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "apple",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 6, chocolate: 6, honey: 4 },
    bgColor: "from-pink-500 to-rose-500",
    borderColor: "border-pink-600",
  },
  {
    id: 115,
    name: "Level 115",
    subtitle: "Point Overlord",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "pear",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 58,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 8, chain: 6, chocolate: 4 },
    bgColor: "from-pink-500 to-rose-500",
    borderColor: "border-pink-600",
  },
  {
    id: 116,
    name: "Level 116",
    subtitle: "Score Supreme",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "orange",
    targetEmoji: "💎",
    targetCount: 200,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 10, chain: 8, chocolate: 5 },
    bgColor: "from-rose-500 to-red-500",
    borderColor: "border-rose-600",
  },
  {
    id: 117,
    name: "Level 117",
    subtitle: "Point Sovereign",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "peach",
    targetEmoji: "💎",
    targetCount: 200,
    moves: 62,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 10, chain: 8, honey: 6 },
    bgColor: "from-rose-500 to-red-500",
    borderColor: "border-rose-600",
  },
  {
    id: 118,
    name: "Level 118",
    subtitle: "Score Immortal",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "strawberry",
    targetEmoji: "💎",
    targetCount: 200,
    moves: 65,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 12, chain: 10, chocolate: 6, honey: 4 },
    bgColor: "from-rose-500 to-red-500",
    borderColor: "border-rose-600",
  },
  {
    id: 119,
    name: "Level 119",
    subtitle: "Point Deity",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "watermelon",
    targetEmoji: "💎",
    targetCount: 200,
    moves: 68,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 15, chain: 12, chocolate: 8, honey: 6 },
    bgColor: "from-rose-500 to-red-500",
    borderColor: "border-rose-600",
  },
  {
    id: 120,
    name: "Level 120",
    subtitle: "POINT WORLD CHAMPION",
    difficulty: "Hard",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "👑",
    targetCount: 200,
    moves: 75,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 18, chain: 15, chocolate: 10, honey: 8 },
    bgColor: "from-yellow-400 via-orange-400 to-red-500",
    borderColor: "border-yellow-600",
  },
  
  // === DARK FOREST WORLD (121-140) ===
  // New obstacles: vines, tree_sap, bird_nest, hollow_knot
  
  // ROOTS SECTION (121-125) - Underground, dark, introduce vines
  {
    id: 121,
    name: "Level 121",
    subtitle: "Into the Shadows",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 49,
    moves: 31,
    boardSize: 9,
    obstacles: { type: "vines", count: 8 },
    bgColor: "from-slate-900 to-emerald-950",
    borderColor: "border-emerald-800",
  },
  {
    id: 122,
    name: "Level 122",
    subtitle: "Root Maze",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 55,
    moves: 28,
    boardSize: 9,
    obstacles: { type: "mixed", vines: 10, ice: 6 },
    bgColor: "from-gray-900 to-green-950",
    borderColor: "border-green-900",
  },
  {
    id: 123,
    name: "Level 123",
    subtitle: "Deep Roots",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "pear",
    targetEmoji: "🍐",
    targetCount: 60,
    moves: 27,
    boardSize: 9,
    obstacles: { type: "mixed", vines: 12, chain: 8 },
    bgColor: "from-neutral-900 to-teal-950",
    borderColor: "border-teal-900",
  },
  {
    id: 124,
    name: "Level 124",
    subtitle: "Underground River",
    difficulty: "Expert",
    missionType: "collect_ingredients",
    targetFruit: "apple",
    targetEmoji: "💎",
    targetCount: 6,
    moves: 31,
    boardSize: 9,
    obstacles: { type: "mixed", vines: 10, honey: 5 },
    bgColor: "from-zinc-900 to-cyan-950",
    borderColor: "border-cyan-900",
  },
  {
    id: 125,
    name: "Level 125",
    subtitle: "Root's End",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 66,
    moves: 25,
    boardSize: 9,
    obstacles: { type: "mixed", vines: 15, ice: 8, chain: 5 },
    bgColor: "from-stone-900 to-lime-950",
    borderColor: "border-lime-900",
  },
  
  // TRUNK SECTION (126-130) - Vertical levels, introduce tree sap
  {
    id: 126,
    name: "Level 126",
    subtitle: "The Great Trunk",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 60,
    moves: 28,
    boardSize: 9,
    obstacles: { type: "tree_sap", count: 10 },
    bgColor: "from-amber-950 to-stone-900",
    borderColor: "border-amber-900",
  },
  {
    id: 127,
    name: "Level 127",
    subtitle: "Sap Trap",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 66,
    moves: 27,
    boardSize: 9,
    obstacles: { type: "mixed", tree_sap: 12, vines: 8 },
    bgColor: "from-yellow-950 to-neutral-900",
    borderColor: "border-yellow-900",
  },
  {
    id: 128,
    name: "Level 128",
    subtitle: "Sticky Situation",
    difficulty: "Expert",
    missionType: "reach_score",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 200,
    moves: 31,
    boardSize: 9,
    obstacles: { type: "mixed", tree_sap: 15, chain: 10 },
    bgColor: "from-orange-950 to-zinc-900",
    borderColor: "border-orange-900",
  },
  {
    id: 129,
    name: "Level 129",
    subtitle: "Amber Prison",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 71,
    moves: 25,
    boardSize: 9,
    obstacles: { type: "mixed", tree_sap: 18, ice: 10, vines: 6 },
    bgColor: "from-red-950 to-gray-900",
    borderColor: "border-red-900",
  },
  {
    id: 130,
    name: "Level 130",
    subtitle: "Trunk Summit",
    difficulty: "Expert",
    missionType: "collect_ingredients",
    targetFruit: "watermelon",
    targetEmoji: "💎",
    targetCount: 8,
    moves: 34,
    boardSize: 9,
    obstacles: { type: "mixed", tree_sap: 15, vines: 12, chocolate: 8 },
    bgColor: "from-emerald-950 to-slate-900",
    borderColor: "border-emerald-900",
  },
  
  // BRANCHES SECTION (131-135) - Split paths, introduce bird nests
  {
    id: 131,
    name: "Level 131",
    subtitle: "First Branch",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 60,
    moves: 27,
    boardSize: 9,
    obstacles: { type: "bird_nest", count: 4 },
    bgColor: "from-teal-950 to-stone-900",
    borderColor: "border-teal-800",
  },
  {
    id: 132,
    name: "Level 132",
    subtitle: "Nest Guardians",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 66,
    moves: 28,
    boardSize: 9,
    obstacles: { type: "mixed", bird_nest: 5, vines: 10 },
    bgColor: "from-cyan-950 to-neutral-900",
    borderColor: "border-cyan-800",
  },
  {
    id: 133,
    name: "Level 133",
    subtitle: "Hollow Passage",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 71,
    moves: 25,
    boardSize: 10,
    obstacles: { type: "mixed", bird_nest: 6, tree_sap: 12, hollow_knot: 2 },
    bgColor: "from-violet-950 to-zinc-900",
    borderColor: "border-violet-900",
  },
  {
    id: 134,
    name: "Level 134",
    subtitle: "Shadow Nests",
    difficulty: "Expert",
    missionType: "reach_score",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 200,
    moves: 31,
    boardSize: 10,
    obstacles: { type: "mixed", bird_nest: 7, chain: 12, honey: 8 },
    bgColor: "from-purple-950 to-gray-900",
    borderColor: "border-purple-900",
  },
  {
    id: 135,
    name: "Level 135",
    subtitle: "Branch's End",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "mango",
    targetEmoji: "🥭",
    targetCount: 77,
    moves: 27,
    boardSize: 10,
    obstacles: { type: "mixed", bird_nest: 8, vines: 15, tree_sap: 10 },
    bgColor: "from-fuchsia-950 to-slate-900",
    borderColor: "border-fuchsia-900",
  },
  
  // CANOPY SECTION (136-139) - Open, all mechanics combined
  {
    id: 136,
    name: "Level 136",
    subtitle: "Canopy Opening",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 71,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", vines: 12, tree_sap: 10, bird_nest: 4, ice: 8 },
    bgColor: "from-green-950 to-emerald-900",
    borderColor: "border-green-800",
  },
  {
    id: 137,
    name: "Level 137",
    subtitle: "Moonlit Leaves",
    difficulty: "Expert",
    missionType: "collect_ingredients",
    targetFruit: "banana",
    targetEmoji: "💎",
    targetCount: 11,
    moves: 36,
    boardSize: 10,
    obstacles: { type: "mixed", vines: 15, tree_sap: 12, chain: 10, hollow_knot: 3 },
    bgColor: "from-indigo-950 to-slate-900",
    borderColor: "border-indigo-800",
  },
  {
    id: 138,
    name: "Level 138",
    subtitle: "Starlight Forest",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 65,
    moves: 30,
    boardSize: 10,
    obstacles: { type: "mixed", bird_nest: 8, vines: 18, chocolate: 10, honey: 6 },
    bgColor: "from-sky-950 to-cyan-900",
    borderColor: "border-sky-800",
  },
  {
    id: 139,
    name: "Level 139",
    subtitle: "Treetop Terror",
    difficulty: "Expert",
    missionType: "reach_score",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 15000,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "mixed", vines: 20, tree_sap: 15, bird_nest: 6, ice: 12, chain: 8 },
    bgColor: "from-rose-950 to-purple-900",
    borderColor: "border-rose-800",
  },
  
  // BOSS LEVEL 140 - The Owl Guardian
  {
    id: 140,
    name: "Level 140",
    subtitle: "THE OWL GUARDIAN",
    difficulty: "BOSS",
    missionType: "boss_fight",
    levelType: "boss_fight",
    targetFruit: "watermelon",
    targetEmoji: "🦉",
    targetCount: 1, // Defeat the boss
    moves: 999, // Unlimited moves
    boardSize: 9, // 9x9 for this boss
    freeRetry: true, // No HP cost for retrying
    boss: {
      name: "The Owl Guardian",
      type: "owl",
      image: null, // Uses emoji-based visual
      phases: [
        { hp: 9000, weakSpot: "blueberry", weakEmoji: "🫐", attack: "night_terror", attackName: "Night Terror" }
      ],
      attackInterval: 4, // Attack every 4 moves
      baseDamage: 8, // Damage per fruit matched
      weakSpotMultiplier: 2.0, // Weak spot does 2x damage
      comboMultiplier: 1.5, // Chain reactions multiply damage
    },
    rewards: {
      stars: 150,
      xp: 8000,
      frame: "owl_vanquisher",
      frameName: "Owl Vanquisher"
    },
    obstacles: { type: "mixed", vines: 8, tree_sap: 6 },
    bgColor: "from-slate-950 via-purple-950 to-indigo-950",
    borderColor: "border-purple-600",
  },

  // ============================================
  // === OCEAN DEPTHS WORLD (141-160) ===
  // ============================================
  {
    id: 141,
    name: "Level 141",
    subtitle: "Diving In",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 55,
    moves: 31,
    boardSize: 10,
    obstacles: { type: "bubbles", count: 8 },
    bgColor: "from-blue-400 to-cyan-600",
    borderColor: "border-blue-500",
  },
  {
    id: 142,
    name: "Level 142",
    subtitle: "Bubble Stream",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 60,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 12, ice: 6 },
    bgColor: "from-cyan-500 to-blue-700",
    borderColor: "border-cyan-600",
  },
  {
    id: 143,
    name: "Level 143",
    subtitle: "Coral Garden",
    difficulty: "Expert",
    missionType: "create_striped",
    targetFruit: "cherry",
    targetEmoji: "⚡",
    targetCount: 6,
    moves: 27,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 10, chain: 8 },
    bgColor: "from-teal-500 to-cyan-700",
    borderColor: "border-teal-600",
  },
  {
    id: 144,
    name: "Level 144",
    subtitle: "Shipwreck",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "pear",
    targetEmoji: "🍐",
    targetCount: 60,
    moves: 34,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 8, anchors: 6 },
    bgColor: "from-gray-600 to-blue-800",
    borderColor: "border-gray-700",
  },
  {
    id: 145,
    name: "Level 145",
    subtitle: "Seaweed Forest",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "pear",
    targetEmoji: "🍐",
    targetCount: 66,
    moves: 27,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 15, vines: 10 },
    bgColor: "from-emerald-600 to-cyan-800",
    borderColor: "border-emerald-700",
  },
  {
    id: 146,
    name: "Level 146",
    subtitle: "Ink Cloud",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "grape",
    targetEmoji: "🍇",
    targetCount: 60,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "octopus_ink", count: 6 },
    bgColor: "from-indigo-600 to-blue-900",
    borderColor: "border-indigo-700",
  },
  {
    id: 147,
    name: "Level 147",
    subtitle: "Anchor Drop",
    difficulty: "Expert",
    missionType: "break_chains",
    targetFruit: "apple",
    targetEmoji: "⛓️",
    targetCount: 13,
    moves: 31,
    boardSize: 10,
    obstacles: { type: "mixed", chain: 12, anchors: 6, bubbles: 4 },
    bgColor: "from-slate-600 to-blue-900",
    borderColor: "border-slate-700",
  },
  {
    id: 148,
    name: "Level 148",
    subtitle: "Strong Currents",
    difficulty: "Expert",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 34,
    boardSize: 10,
    obstacles: { type: "mixed", currents: 8, octopus_ink: 4 },
    bgColor: "from-blue-700 to-indigo-900",
    borderColor: "border-blue-800",
  },
  {
    id: 149,
    name: "Level 149",
    subtitle: "Treasure Hunt",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 71,
    moves: 27,
    boardSize: 10,
    obstacles: { type: "mixed", anchors: 10, chain: 12, bubbles: 6 },
    bgColor: "from-amber-600 to-blue-900",
    borderColor: "border-amber-700",
  },
  {
    id: 150,
    name: "Level 150",
    subtitle: "Bioluminescence",
    difficulty: "Expert",
    missionType: "create_striped",
    targetFruit: "striped",
    targetEmoji: "🌈",
    targetCount: 8,
    moves: 36,
    boardSize: 10,
    obstacles: { type: "mixed", octopus_ink: 8, anchors: 8, bubbles: 10 },
    bgColor: "from-purple-700 to-blue-950",
    borderColor: "border-purple-800",
  },
  {
    id: 151,
    name: "Level 151",
    subtitle: "Into the Abyss",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 72,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 12, octopus_ink: 6, currents: 4 },
    bgColor: "from-slate-800 to-blue-950",
    borderColor: "border-slate-900",
  },
  {
    id: 152,
    name: "Level 152",
    subtitle: "Pressure Zone",
    difficulty: "Master",
    missionType: "create_wrapped",
    targetFruit: "lemon",
    targetEmoji: "🎁",
    targetCount: 9,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "mixed", anchors: 15, bubbles: 10 },
    bgColor: "from-indigo-800 to-slate-950",
    borderColor: "border-indigo-900",
  },
  {
    id: 153,
    name: "Level 153",
    subtitle: "Giant Squid",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "blueberry",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 32,
    boardSize: 10,
    obstacles: { type: "mixed", octopus_ink: 10, honey: 8 },
    bgColor: "from-rose-800 to-indigo-950",
    borderColor: "border-rose-900",
  },
  {
    id: 154,
    name: "Level 154",
    subtitle: "Volcanic Vents",
    difficulty: "Master",
    missionType: "break_ice",
    targetFruit: "peach",
    targetEmoji: "🧊",
    targetCount: 30,
    moves: 33,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 15, ice: 25, chocolate: 8 },
    bgColor: "from-orange-700 to-blue-950",
    borderColor: "border-orange-800",
  },
  {
    id: 155,
    name: "Level 155",
    subtitle: "Mariana Trench",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 90,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "mixed", anchors: 18, octopus_ink: 10, currents: 6 },
    bgColor: "from-slate-900 to-black",
    borderColor: "border-slate-950",
  },
  {
    id: 156,
    name: "Level 156",
    subtitle: "Frozen Chains",
    difficulty: "Master",
    missionType: "break_chains",
    targetFruit: "apple",
    targetEmoji: "⛓️",
    targetCount: 12,
    moves: 32,
    boardSize: 10,
    obstacles: { type: "mixed", chain: 10, ice: 10, anchors: 5 },
    bgColor: "from-cyan-900 to-slate-950",
    borderColor: "border-cyan-950",
  },
  {
    id: 157,
    name: "Level 157",
    subtitle: "Fruit Frenzy",
    difficulty: "Master",
    missionType: "dual_collect",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 120,
    // Dual mission objectives
    dualMission: {
      objective1: { fruit: "banana", emoji: "🍌", count: 100 },
      objective2: { fruit: "cherry", emoji: "🍒", count: 100 },
    },
    moves: 44,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 8, chain: 6 },
    bgColor: "from-yellow-700 to-red-900",
    borderColor: "border-yellow-800",
  },
  {
    id: 158,
    name: "Level 158",
    subtitle: "Kraken's Domain",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "kiwi",
    targetEmoji: "👑",
    targetCount: 200,
    moves: 36,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 25, octopus_ink: 15, anchors: 12, currents: 8 },
    bgColor: "from-red-900 to-slate-950",
    borderColor: "border-red-950",
  },
  {
    id: 159,
    name: "Level 159",
    subtitle: "Final Dive",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 102,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", bubbles: 30, octopus_ink: 18, anchors: 15 },
    bgColor: "from-blue-950 to-black",
    borderColor: "border-blue-900",
  },
  {
    id: 160,
    name: "Level 160",
    subtitle: "THE GIANT KRAKEN",
    difficulty: "BOSS",
    missionType: "boss_fight",
    levelType: "boss_fight",
    targetFruit: "kraken",
    targetEmoji: "🦑",
    targetCount: 1,
    moves: 999,
    boardSize: 9,
    freeRetry: true,
    boss: {
      name: "The Giant Kraken",
      type: "kraken",
      phases: [
        { hp: 5000, weakSpot: "orange", weakEmoji: "🍊", attack: "whirlpool", attackName: "Whirlpool" }
      ],
      attackInterval: 4,
      baseDamage: 14,
      weakSpotMultiplier: 2.2,
      comboMultiplier: 1.6,
    },
    rewards: { stars: 200, xp: 10000, frame: "kraken_slayer", frameName: "Kraken Slayer" },
    obstacles: { type: "mixed", bubbles: 10, octopus_ink: 6 },
    bgColor: "from-blue-950 via-indigo-950 to-slate-950",
    borderColor: "border-cyan-600",
  },

  // ============================================
  // === FROZEN PEAKS WORLD (161-180) ===
  // ============================================
  {
    id: 161,
    name: "Level 161",
    subtitle: "Base Camp",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 60,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "frozen_fruits", count: 10 },
    bgColor: "from-sky-300 to-blue-500",
    borderColor: "border-sky-400",
  },
  {
    id: 162,
    name: "Level 162",
    subtitle: "Frozen Trail",
    difficulty: "Master",
    missionType: "break_ice",
    targetFruit: "pear",
    targetEmoji: "🧊",
    targetCount: 18,
    moves: 25,
    boardSize: 10,
    obstacles: { type: "mixed", frozen_fruits: 12, ice: 15 },
    bgColor: "from-cyan-300 to-sky-600",
    borderColor: "border-cyan-500",
  },
  {
    id: 163,
    name: "Level 163",
    subtitle: "Snowdrift Valley",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "grape",
    targetEmoji: "🍇",
    targetCount: 66,
    moves: 24,
    boardSize: 10,
    obstacles: { type: "mixed", snowdrift: 8, frozen_fruits: 10 },
    bgColor: "from-white to-sky-400",
    borderColor: "border-blue-400",
  },
  {
    id: 164,
    name: "Level 164",
    subtitle: "Ice Bridge",
    difficulty: "Master",
    missionType: "break_ice",
    targetFruit: "ice",
    targetEmoji: "🧊",
    targetCount: 20,
    moves: 30,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 20, chain: 8 },
    bgColor: "from-slate-200 to-cyan-600",
    borderColor: "border-slate-400",
  },
  {
    id: 165,
    name: "Level 165",
    subtitle: "Glacier Pass",
    difficulty: "Master",
    missionType: "create_striped",
    targetFruit: "mango",
    targetEmoji: "⚡",
    targetCount: 9,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", frozen_fruits: 15, snowdrift: 10, ice: 8 },
    bgColor: "from-blue-200 to-indigo-600",
    borderColor: "border-blue-500",
  },
  {
    id: 166,
    name: "Level 166",
    subtitle: "First Blizzard",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 72,
    moves: 25,
    boardSize: 10,
    obstacles: { type: "mixed", blizzard: 4, frozen_fruits: 12 },
    bgColor: "from-gray-300 to-blue-700",
    borderColor: "border-gray-500",
  },
  {
    id: 167,
    name: "Level 167",
    subtitle: "Whiteout",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "lemon",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", blizzard: 6, snowdrift: 12, ice: 10 },
    bgColor: "from-slate-100 to-gray-600",
    borderColor: "border-slate-400",
  },
  {
    id: 168,
    name: "Level 168",
    subtitle: "Frozen Falls",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "peach",
    targetEmoji: "🍑",
    targetCount: 60,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", frozen_fruits: 18, blizzard: 5, chain: 10 },
    bgColor: "from-teal-300 to-blue-800",
    borderColor: "border-teal-500",
  },
  {
    id: 169,
    name: "Level 169",
    subtitle: "Crystal Cavern",
    difficulty: "Master",
    missionType: "create_wrapped",
    targetFruit: "banana",
    targetEmoji: "🎁",
    targetCount: 5,
    moves: 35,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 25, frozen_fruits: 15 },
    bgColor: "from-violet-300 to-indigo-700",
    borderColor: "border-violet-500",
  },
  {
    id: 170,
    name: "Level 170",
    subtitle: "Avalanche Zone",
    difficulty: "Master",
    missionType: "break_ice",
    targetFruit: "orange",
    targetEmoji: "🧊",
    targetCount: 36,
    moves: 33,
    boardSize: 10,
    obstacles: { type: "mixed", avalanche: 6, snowdrift: 15, ice: 30 },
    bgColor: "from-red-200 to-blue-800",
    borderColor: "border-red-400",
  },
  {
    id: 171,
    name: "Level 171",
    subtitle: "Storm Front",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 65,
    moves: 30,
    boardSize: 10,
    obstacles: { type: "mixed", blizzard: 8, frozen_fruits: 20, snowdrift: 12 },
    bgColor: "from-purple-400 to-blue-900",
    borderColor: "border-purple-600",
  },
  {
    id: 172,
    name: "Level 172",
    subtitle: "Ice Palace",
    difficulty: "Master",
    missionType: "combo_mission",
    targetFruit: "combo",
    targetEmoji: "💥",
    targetCount: 15,
    moves: 55,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 35, frozen_fruits: 18, chain: 12 },
    bgColor: "from-cyan-200 to-purple-800",
    borderColor: "border-cyan-400",
  },
  {
    id: 173,
    name: "Level 173",
    subtitle: "Permafrost",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 90,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "mixed", frozen_fruits: 25, avalanche: 8, blizzard: 6 },
    bgColor: "from-slate-400 to-indigo-900",
    borderColor: "border-slate-600",
  },
  {
    id: 174,
    name: "Level 174",
    subtitle: "Northern Lights",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "watermelon",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 36,
    boardSize: 10,
    obstacles: { type: "mixed", snowdrift: 20, honey: 10, ice: 15 },
    bgColor: "from-green-400 via-purple-500 to-pink-500",
    borderColor: "border-green-500",
  },
  {
    id: 175,
    name: "Level 175",
    subtitle: "Death Zone",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 96,
    moves: 42,
    boardSize: 10,
    obstacles: { type: "mixed", blizzard: 10, avalanche: 10, frozen_fruits: 22 },
    bgColor: "from-gray-600 to-slate-900",
    borderColor: "border-gray-700",
  },
  {
    id: 176,
    name: "Level 176",
    subtitle: "Frozen Throne",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 18000,
    moves: 42,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 30, frozen_fruits: 15, chain: 10 },
    bgColor: "from-blue-300 to-slate-900",
    borderColor: "border-blue-500",
  },
  {
    id: 177,
    name: "Level 177",
    subtitle: "Eternal Winter",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 102,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", blizzard: 12, snowdrift: 25, avalanche: 8, frozen_fruits: 18 },
    bgColor: "from-indigo-400 to-purple-950",
    borderColor: "border-indigo-600",
  },
  {
    id: 178,
    name: "Level 178",
    subtitle: "Arctic Vortex",
    difficulty: "Master",
    missionType: "break_ice",
    targetFruit: "apple",
    targetEmoji: "🧊",
    targetCount: 54,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "mixed", ice: 45, frozen_fruits: 25, blizzard: 10 },
    bgColor: "from-cyan-500 to-blue-950",
    borderColor: "border-cyan-600",
  },
  {
    id: 179,
    name: "Level 179",
    subtitle: "Giant's Footsteps",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "💎",
    targetCount: 200,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "mixed", avalanche: 12, snowdrift: 30, frozen_fruits: 28, blizzard: 8 },
    bgColor: "from-slate-500 to-indigo-950",
    borderColor: "border-slate-700",
  },
  {
    id: 180,
    name: "Level 180",
    subtitle: "THE FROST GIANT",
    difficulty: "BOSS",
    missionType: "boss_fight",
    levelType: "boss_fight",
    targetFruit: "frost_giant",
    targetEmoji: "🥶",
    targetCount: 1,
    moves: 999,
    boardSize: 9,
    freeRetry: true,
    boss: {
      name: "The Frost Giant",
      type: "frost_giant",
      phases: [
        { hp: 5400, weakSpot: "cherry", weakEmoji: "🍒", attack: "glacier_smash", attackName: "Glacier Smash" }
      ],
      attackInterval: 4,
      baseDamage: 15,
      weakSpotMultiplier: 2.0,
      comboMultiplier: 1.7,
    },
    rewards: { stars: 250, xp: 12000, frame: "frost_conqueror", frameName: "Frost Conqueror" },
    obstacles: { type: "mixed", frozen_fruits: 8, ice: 10 },
    bgColor: "from-sky-300 via-blue-700 to-indigo-950",
    borderColor: "border-sky-400",
  },

  // ============================================
  // === ANCIENT RUINS WORLD (181-200) ===
  // ============================================
  {
    id: 181,
    name: "Level 181",
    subtitle: "Temple Gates",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "banana",
    targetEmoji: "🍌",
    targetCount: 66,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "stone_idols", count: 6 },
    bgColor: "from-amber-500 to-stone-700",
    borderColor: "border-amber-600",
  },
  {
    id: 182,
    name: "Level 182",
    subtitle: "Sand Corridor",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 72,
    moves: 25,
    boardSize: 10,
    obstacles: { type: "mixed", sand_traps: 10, stone_idols: 4 },
    bgColor: "from-yellow-500 to-amber-800",
    borderColor: "border-yellow-600",
  },
  {
    id: 183,
    name: "Level 183",
    subtitle: "Hieroglyph Hall",
    difficulty: "Master",
    missionType: "create_striped",
    targetFruit: "grape",
    targetEmoji: "⚡",
    targetCount: 8,
    moves: 42,
    boardSize: 10,
    obstacles: { type: "mixed", chain: 15, stone_idols: 6 },
    bgColor: "from-stone-500 to-gray-800",
    borderColor: "border-stone-600",
  },
  {
    id: 184,
    name: "Level 184",
    subtitle: "Trapped Chamber",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "orange",
    targetEmoji: "🍊",
    targetCount: 70,
    moves: 32,
    boardSize: 10,
    obstacles: { type: "mixed", sand_traps: 12, lock_key: 4 },
    bgColor: "from-emerald-700 to-stone-900",
    borderColor: "border-emerald-800",
  },
  {
    id: 185,
    name: "Level 185",
    subtitle: "Cursed Passage",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 60,
    moves: 28,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 8, sand_traps: 8, stone_idols: 4 },
    bgColor: "from-purple-700 to-stone-900",
    borderColor: "border-purple-800",
  },
  {
    id: 186,
    name: "Level 186",
    subtitle: "Spreading Curse",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 65,
    moves: 30,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 12, chocolate: 10 },
    bgColor: "from-violet-800 to-gray-950",
    borderColor: "border-violet-900",
  },
  {
    id: 187,
    name: "Level 187",
    subtitle: "Lock & Key",
    difficulty: "Master",
    missionType: "break_chains",
    targetFruit: "pear",
    targetEmoji: "🔐",
    targetCount: 19,
    moves: 30,
    boardSize: 10,
    obstacles: { type: "mixed", lock_key: 8, chain: 16, stone_idols: 6 },
    bgColor: "from-amber-700 to-slate-900",
    borderColor: "border-amber-800",
  },
  {
    id: 188,
    name: "Level 188",
    subtitle: "Quicksand Pit",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "peach",
    targetEmoji: "🎯",
    targetCount: 200,
    moves: 32,
    boardSize: 10,
    obstacles: { type: "mixed", sand_traps: 18, honey: 10, curse_tiles: 6 },
    bgColor: "from-yellow-700 to-amber-950",
    borderColor: "border-yellow-800",
  },
  {
    id: 189,
    name: "Level 189",
    subtitle: "Tomb Raiders",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 90,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "mixed", stone_idols: 10, sand_traps: 15, lock_key: 6 },
    bgColor: "from-stone-700 to-slate-950",
    borderColor: "border-stone-800",
  },
  {
    id: 190,
    name: "Level 190",
    subtitle: "Treasure Vault",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 18000,
    moves: 40,
    boardSize: 10,
    obstacles: { type: "mixed", lock_key: 10, chain: 20, stone_idols: 8 },
    bgColor: "from-yellow-500 to-stone-900",
    borderColor: "border-yellow-600",
  },
  {
    id: 191,
    name: "Level 191",
    subtitle: "Mummy's Curse",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 96,
    moves: 42,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 15, sand_traps: 12, vines: 10 },
    bgColor: "from-teal-800 to-slate-950",
    borderColor: "border-teal-900",
  },
  {
    id: 192,
    name: "Level 192",
    subtitle: "Hall of Mirrors",
    difficulty: "Master",
    missionType: "create_wrapped",
    targetFruit: "lemon",
    targetEmoji: "🎁",
    targetCount: 12,
    moves: 60,
    boardSize: 10,
    obstacles: { type: "mixed", stone_idols: 12, ice: 20, lock_key: 6 },
    bgColor: "from-cyan-700 to-indigo-950",
    borderColor: "border-cyan-800",
  },
  {
    id: 193,
    name: "Level 193",
    subtitle: "Serpent's Path",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "watermelon",
    targetEmoji: "🍉",
    targetCount: 102,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 18, sand_traps: 15, chain: 12 },
    bgColor: "from-green-800 to-stone-950",
    borderColor: "border-green-900",
  },
  {
    id: 194,
    name: "Level 194",
    subtitle: "Anubis Trial",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "mango",
    targetEmoji: "🏆",
    targetCount: 200,
    moves: 38,
    boardSize: 10,
    obstacles: { type: "mixed", stone_idols: 15, curse_tiles: 12, honey: 10 },
    bgColor: "from-slate-700 to-black",
    borderColor: "border-slate-800",
  },
  {
    id: 195,
    name: "Level 195",
    subtitle: "Sphinx's Riddle",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "pineapple",
    targetEmoji: "🍍",
    targetCount: 108,
    moves: 48,
    boardSize: 10,
    obstacles: { type: "mixed", lock_key: 12, sand_traps: 20, stone_idols: 10 },
    bgColor: "from-amber-800 to-purple-950",
    borderColor: "border-amber-900",
  },
  {
    id: 196,
    name: "Level 196",
    subtitle: "Golden Sarcophagus",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 50000,
    moves: 100,
    boardSize: 10,
    comboBonus: true,
    obstacles: { type: "mixed", chain: 25, stone_idols: 12, curse_tiles: 10 },
    bgColor: "from-yellow-600 to-amber-950",
    borderColor: "border-yellow-700",
  },
  {
    id: 197,
    name: "Level 197",
    subtitle: "Plague of Locusts",
    difficulty: "Master",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 114,
    moves: 50,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 20, sand_traps: 18, chocolate: 15 },
    bgColor: "from-lime-800 to-stone-950",
    borderColor: "border-lime-900",
  },
  {
    id: 198,
    name: "Level 198",
    subtitle: "Eye of Horus",
    difficulty: "Master",
    missionType: "dual_collect",
    targetFruit: "apple",
    targetEmoji: "🍎",
    targetCount: 140,
    dualMission: {
      objective1: { fruit: "apple", emoji: "🍎", count: 70 },
      objective2: { fruit: "grape", emoji: "🍇", count: 70 },
    },
    moves: 65,
    boardSize: 10,
    obstacles: { type: "mixed", lock_key: 14, stone_idols: 15, curse_tiles: 12 },
    bgColor: "from-blue-800 to-indigo-950",
    borderColor: "border-blue-900",
  },
  {
    id: 199,
    name: "Level 199",
    subtitle: "Final Judgment",
    difficulty: "Master",
    missionType: "reach_score",
    targetFruit: "score",
    targetEmoji: "⭐",
    targetCount: 20000,
    moves: 45,
    boardSize: 10,
    obstacles: { type: "mixed", curse_tiles: 25, sand_traps: 22, stone_idols: 15, lock_key: 10 },
    bgColor: "from-rose-800 to-slate-950",
    borderColor: "border-rose-900",
  },
  {
    id: 200,
    name: "Level 200",
    subtitle: "PHARAOH'S GUARDIAN",
    difficulty: "BOSS",
    missionType: "boss_fight",
    levelType: "boss_fight",
    targetFruit: "pharaoh",
    targetEmoji: "🐪",
    targetCount: 1,
    moves: 999,
    boardSize: 9,
    freeRetry: true,
    boss: {
      name: "Pharaoh's Guardian",
      type: "pharaoh",
      phases: [
        { hp: 7000, weakSpot: "banana", weakEmoji: "🍌", attack: "tomb_collapse", attackName: "Tomb Collapse" }
      ],
      attackInterval: 4,
      baseDamage: 16,
      weakSpotMultiplier: 2.0,
      comboMultiplier: 1.8,
    },
    rewards: { stars: 300, xp: 15000, frame: "pharaoh_vanquisher", frameName: "Pharaoh's Vanquisher" },
    obstacles: { type: "mixed", stone_idols: 6, curse_tiles: 8 },
    bgColor: "from-amber-600 via-stone-800 to-slate-950",
    borderColor: "border-amber-500",
  },
];

// Import new world levels
import { ALL_NEW_LEVELS } from './newWorlds';

// Combine all levels
export const ALL_LEVELS = [...LEVELS, ...ALL_NEW_LEVELS];

// Import member and VIP levels for extended level lookup
import { getMemberLevelById } from './memberWorld';
import { getVIPLevelById } from './vipWorld';

// Get level by ID
export const getLevelById = (id) => {
  // Check VIP world levels (8001-8050)
  if (id >= 8001 && id <= 8050) {
    const vipLevel = getVIPLevelById(id);
    if (vipLevel) return vipLevel;
  }
  
  // Check Member world levels (9001-9050)
  if (id >= 9001 && id <= 9050) {
    const memberLevel = getMemberLevelById(id);
    if (memberLevel) return memberLevel;
  }
  
  // Regular levels
  return ALL_LEVELS.find(level => level.id === id) || ALL_LEVELS[0];
};

// Check if level is unlocked based on completed levels
export const isLevelUnlocked = (levelId, completedLevels) => {
  if (levelId === 1) return true;
  return completedLevels.includes(levelId - 1);
};

// Get completed levels from localStorage
export const getCompletedLevels = () => {
  const completed = localStorage.getItem('fruitCrush_completedLevels');
  return completed ? JSON.parse(completed) : [];
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Sync level completion to backend
const syncLevelToBackend = async (completedLevels, levelScores) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  try {
    await fetch(`${API_URL}/api/progress`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ completed_levels: completedLevels, level_scores: levelScores })
    });
  } catch (error) {
    console.error('Failed to sync level:', error);
  }
};

// Mark level as completed
export const markLevelCompleted = (levelId) => {
  const completed = getCompletedLevels();
  if (!completed.includes(levelId)) {
    completed.push(levelId);
    localStorage.setItem('fruitCrush_completedLevels', JSON.stringify(completed));
    
    // Sync to backend
    const scores = JSON.parse(localStorage.getItem('fruitCrush_levelScores') || '{}');
    syncLevelToBackend(completed, scores);
  }
  return completed;
};

// Get best score for a level
export const getLevelBestScore = (levelId) => {
  const scores = localStorage.getItem('fruitCrush_levelScores');
  const scoresObj = scores ? JSON.parse(scores) : {};
  return scoresObj[levelId] || 0;
};

// Save level score
export const saveLevelScore = (levelId, score) => {
  const scores = localStorage.getItem('fruitCrush_levelScores');
  const scoresObj = scores ? JSON.parse(scores) : {};
  if (!scoresObj[levelId] || score > scoresObj[levelId]) {
    scoresObj[levelId] = score;
    localStorage.setItem('fruitCrush_levelScores', JSON.stringify(scoresObj));
    
    // Sync to backend
    const completed = getCompletedLevels();
    syncLevelToBackend(completed, scoresObj);
  }
  return scoresObj[levelId];
};

// Get stars for a level based on score
export const getLevelStars = (levelId, score) => {
  const level = getLevelById(levelId);
  const baseScore = level.targetCount * 10;
  
  if (score >= baseScore * 3) return 3;
  if (score >= baseScore * 2) return 2;
  if (score >= baseScore) return 1;
  return 0;
};

// Total number of levels
export const TOTAL_LEVELS = ALL_LEVELS.length;
